import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { BlogService } from "../services/api/blog.service";
import { Edit3, Trash2, Plus, Search } from "lucide-react";
import VideoForm from "../shared/VideoForm";
import { DataTable } from "./DataTable/DataTable";

export interface VideoBlog {
    id: number;
    path: string;
    category: string;
    title: string;
    description: string;
    url: string;
}
export interface BlogApiResponse {
    success: boolean;
    data: {
        data: VideoBlog[],
        totalData: number,
        totalPages: number,
        currentPage: number,
        hasNext: boolean,
        hasPrev: boolean
    }
}
const VideoBlogs = () => {
    const [videos, setVideos] = useState<VideoBlog[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [showAddVideos, setShowAddVideos] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedVideo, setSelectedVideo] = useState<VideoBlog | null>(null);
    const loadVideos = async (page = 1, pageSize = 10, search = "") => {
        try {
            setIsLoading(true);
            const response: BlogApiResponse = await BlogService.fetchVideos(page, pageSize, search);
            if (response.success) {
                setVideos(response.data.data);
                setTotalPages(response.data.totalPages);
                setTotalItemsCount(response.data.totalData);
                setCurrentPage(response.data.currentPage);
            } else {
                toast.error("Failed to load videos");
            }
        } catch (error) {
            toast.error("Error fetching videos");
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadVideos(currentPage, pageSize, searchText);
    }, [currentPage, pageSize, searchText]);
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleAddVideoSuccess = () => {
        setShowAddVideos(false);
        loadVideos();
    };

    const handleEditClick = (video: VideoBlog) => {
        setSelectedVideo(video);
        setShowAddVideos(true);
    };

    const handleDeleteVideo = async (videoId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        try {
            const deleteVideoResponse = await BlogService.deleteVideos(videoId);

            if (deleteVideoResponse.success) {
                toast.success("Video deleted successfully");
                loadVideos();
            } else {
                toast.error("Failed to delete video");
            }
            console.log("\n\n deleteVideoResponse:", deleteVideoResponse, "\n\n");
        } catch (error) {
            console.error(error);
        }
    };

    const columns = [
        {
            key: "title",
            title: "Title",
            sortable: true,
            width: "w-[200px]",
            render: (video: VideoBlog) => (
                <div>
                    <div className="font-medium">{video.title}</div>
                </div>
            )
        },
        {
            key: "category",
            title: "Category",
            sortable: true,
            width: "w-[150px]",
            render: (video: VideoBlog) => (
                <div className="font-medium capitalize text-blue-600">
                    {video.category}
                </div>
            ),
        },
        {
            key: "description",
            title: "description",
            sortable: true,
            width: "w-[150px]",
            render: (video: VideoBlog) => (
                <div className="font-medium capitalize text-blue-600">
                    {video.description}
                </div>
            ),
        },
        {
            key: "actions",
            title: "Actions",
            width: "w-[200px]",
            render: (blog: VideoBlog) => (
                <div className="flex items-center gap-2">

                    <button
                        className="text-green-600 hover:text-green-700"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEditClick(blog);
                        }}
                        title={`Edit ${blog.title}`}
                    >
                        <Edit3 className="w-5 h-5" />
                    </button>


                    {/* Enable/Disable (Only Admin) */}

                    <button
                        className="rounded-full text-red-600 hover:text-red-700  p-1 px-4"
                        onClick={(event) => handleDeleteVideo(blog.id, event)}
                        title={`Delete ${blog.title}`}
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>

                </div>
            ),
        },
    ]

    const addBlogButton = (
        <button
            onClick={() => setShowAddVideos(true)}
            className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
        >
            <Plus className="w-5 h-5 mr-2" />
            Add Video
        </button>
    );

    return (
        <div className="relative pb-2">
            {!showAddVideos && (
                <div className="flex justify-end relative top-9">
                    <div>
                        <Search className="relative top-[2.4rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search blogs..."
                            value={searchText}
                            onChange={handleSearchChange}
                            className="border rounded-lg px-3 py-2.5 text-sm w-64 text-center"
                        />
                    </div>
                </div>
            )}

            {showAddVideos ? (
                <VideoForm
                onClose={() => setShowAddVideos(false)}
                blogData={selectedVideo}
                handleAddVideoSuccess={handleAddVideoSuccess}
                />
            ) : (
                <DataTable
                    data={videos}
                    columns={columns}
                    itemsPerPage={pageSize}
                    searchable
                    searchFields={["title", "category", "description"]}
                    onRowClick={(video: VideoBlog) => {
                        setSelectedVideo(video);
                        setShowAddVideos(true);
                    }}
                    isLoading={isLoading}
                    customFilters={addBlogButton}
                    pagination={{
                        currentPage,
                        setCurrentPage,
                        totalPages,
                        pageSize,
                        totalItemsCount,
                    }}
                />
            )}
        </div>
    );

}

export default VideoBlogs